// Upvalues that stop in the browser before reaching the server (a queue error, the
// no-coins block, a first-upvalue confirm that never opens) leave no trace on our side:
// most of them are silent to the user too. Report each one to
// /api/v1/upvalue_diagnostics, whose log line reaches Graylog ([upvalue_failure]).
// Diagnostics only: reporting must never affect the upvalue.

import { accessTokens } from 'api/accessTokenMgmt'

export type UpvalueFailureStage =
    | 'queue_failed'
    | 'no_balance_blocked'
    | 'confirm_shown'
    | 'confirm_already_open'
    | 'scheduler_crashed'

export type UpvalueFailureFields = Record<string, string | number | boolean | null | undefined>

// One page can't flood the log: each stage/answer/error once, and a cap per page.
const MAX_REPORTS_PER_PAGE = 30

export function createUpvalueFailureReporter(send: (body: Record<string, unknown>) => void) {
    const reported = new Set<string>()
    return (stage: UpvalueFailureStage, fields: UpvalueFailureFields = {}) => {
        const key = `${stage}:${fields.answer_id ?? ''}:${fields.error ?? ''}`
        if (reported.has(key) || reported.size >= MAX_REPORTS_PER_PAGE) return
        reported.add(key)
        try {
            send({ stage, ...fields })
        } catch {
            // Reporting must never affect the upvalue.
        }
    }
}

export const reportUpvalueFailure = createUpvalueFailureReporter(body => {
    if (typeof window === 'undefined' || typeof fetch !== 'function') return
    const webCommit = (window as unknown as { Knov?: { version?: { gitCommitHash?: string } } })
        .Knov?.version?.gitCommitHash
    void fetch(`${window.knovApiUrl}/api/v1/upvalue_diagnostics`, {
        method: 'POST',
        headers: { ...accessTokens(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
            ...body,
            web_commit: webCommit,
            visible: document.visibilityState === 'visible',
        }),
        keepalive: true,
    }).catch(() => {})
})
